import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { chatHandler } from "../handlers/chat-handler";
import { isAuthenticatedAtom, authTokensAtom } from "./auth";
import { orpcIntegration } from "../integration/orpc-integration";
import { orpcConnectionStatusAtom } from "./integrations";
import { participantIdAtom, loadParticipantIdAtom } from "./participant";
import { workspaceConfig } from "../config/workspace";
import { envConfig } from "../config/env";
import { workspacesAtom, createWorkspaceAtom } from "./workspaces";

import type {
  ChatMessage,
  ChatConversation,
  ChatThread,
  ChatState,
  ChatSettings,
} from "@pineseed/gui/types/chat";

/**
 * Chat State Management - 100% oRPC Based
 * STRICT RULE: Only atoms, no business logic, no UI code, no external calls
 * All business logic handled by chat-handler.ts and orpc-handler.ts
 */

// ===== HANDLER CALLBACK PATTERN =====

// Handler callbacks atom - follows the mandatory callback pattern for handlers
export const chatHandlerCallbacksAtom = atom<{
  onInitializeChatSystem?: (
    workspaceId: string,
    authToken?: string,
  ) => Promise<void>;
  onCleanupChatSystem?: () => Promise<void>;
  // Queueing callbacks
  onAddQueuedMessage?: (
    message: Omit<QueuedMessage, "id" | "timestamp" | "retryCount">,
  ) => string;
  onRemoveQueuedMessage?: (messageId: string) => void;
  onClearQueuedMessages?: () => void;
  onGetQueuedMessagesForConversation?: (
    conversationId: string,
  ) => QueuedMessage[];
}>({});

// ===== WORKSPACE CONTEXT =====

// Active workspace ID atom (imported from workspaces state when available)
export const activeWorkspaceIdAtom = atom<string | null>(null);

// ===== CORE CHAT STATE =====

export const chatStateAtom = atom<
  ChatState & {
    threads: ChatThread[];
    activeThreadId: string | null;
  }
>({
  conversations: [],
  activeConversationId: null,
  threads: [],
  activeThreadId: null,
  isLoading: false,
  isStreaming: false,
  streamingMessageId: null,
  error: null,
  pendingResponses: [],
});

export const chatSettingsAtom = atomWithStorage<ChatSettings>(
  "ive-chat-settings",
  {
    defaultModel: "gpt-4",
    maxTokens: 4000,
    temperature: 0.7,
    systemPrompt: "You are a helpful AI assistant.",
    autoSave: true,
    enableReasoning: true,
    asyncMode: true,
  },
);

// ===== QUEUED MESSAGES STATE =====

export interface QueuedMessage {
  id: string;
  conversationId: string;
  threadId?: string;
  content: string;
  timestamp: Date;
  retryCount: number;
}

export const queuedMessagesAtom = atomWithStorage<QueuedMessage[]>(
  "ive-chat-queued-messages",
  [],
);

export const addQueuedMessageAtom = atom(
  null,
  (
    get,
    set,
    message: Omit<QueuedMessage, "id" | "timestamp" | "retryCount">,
  ) => {
    const queuedMessages = get(queuedMessagesAtom);
    const newMessage: QueuedMessage = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: new Date(),
      retryCount: 0,
    };

    set(queuedMessagesAtom, [...queuedMessages, newMessage]);
    return newMessage.id;
  },
);

export const removeQueuedMessageAtom = atom(
  null,
  (get, set, messageId: string) => {
    const queuedMessages = get(queuedMessagesAtom);
    set(
      queuedMessagesAtom,
      queuedMessages.filter((msg) => msg.id !== messageId),
    );
  },
);

export const getQueuedMessagesForConversationAtom = atom(
  (get) => (conversationId: string) => {
    const queuedMessages = get(queuedMessagesAtom);
    return queuedMessages.filter(
      (msg) => msg.conversationId === conversationId,
    );
  },
);

export const clearQueuedMessagesAtom = atom(null, (_get, set) => {
  set(queuedMessagesAtom, []);
});

// Derived atom for queue count (more efficient for components that only need the count)
export const queuedMessageCountAtom = atom((get) => {
  const queuedMessages = get(queuedMessagesAtom);
  return queuedMessages.length;
});

// ===== DERIVED STATE =====

export const conversationsAtom = atom(
  (get) => get(chatStateAtom).conversations,
);

export const threadsAtom = atom((get) => get(chatStateAtom).threads);

export const activeConversationAtom = atom((get) => {
  const state = get(chatStateAtom);
  return (
    state.conversations.find(
      (conv) => conv.id === state.activeConversationId,
    ) || null
  );
});

export const activeThreadAtom = atom((get) => {
  const state = get(chatStateAtom);
  return (
    state.threads.find((thread) => thread.id === state.activeThreadId) || null
  );
});

// Threads for the active conversation
export const activeConversationThreadsAtom = atom((get) => {
  const state = get(chatStateAtom);
  const activeConversationId = state.activeConversationId;
  if (!activeConversationId) return [];

  return state.threads.filter(
    (thread) => thread.conversationId === activeConversationId,
  );
});

export const isLoadingAtom = atom((get) => get(chatStateAtom).isLoading);

export const isStreamingAtom = atom((get) => get(chatStateAtom).isStreaming);

export const errorAtom = atom((get) => get(chatStateAtom).error);

export const pendingResponsesAtom = atom(
  (get) => get(chatStateAtom).pendingResponses,
);

export const hasPendingResponsesAtom = atom(
  (get) => get(pendingResponsesAtom).length > 0,
);

export const getMessageIdsByConversationIdAtom = atom(
  (get) => (conversationId: string) => {
    const currentState = get(chatStateAtom);
    return (
      currentState.conversations
        .find((conv) => conv.id === conversationId)
        ?.messages?.map((msg) => msg.id) || []
    );
  },
);

// ===== CONVERSATION MANAGEMENT ACTIONS =====

export const setActiveConversationAtom = atom(
  null,
  async (get, set, conversationId: string | null) => {
    const currentState = get(chatStateAtom);
    set(chatStateAtom, {
      ...currentState,
      activeConversationId: conversationId,
      activeThreadId: null, // Reset active thread when switching conversations
    });

    // // Load threads and messages for the selected conversation
    // if (conversationId) {
    //   try {
    //     await chatHandler.loadThreads(conversationId);
    //     await chatHandler.loadMessages(conversationId);
    //   } catch (error) {
    //     console.warn("Failed to load data for conversation:", error);
    //   }
    // }
  },
);

export const addConversationAtom = atom(
  null,
  (get, set, conversation: ChatConversation) => {
    const currentState = get(chatStateAtom);
    const existingIndex = currentState.conversations.findIndex(
      (c) => c.id === conversation.id,
    );

    if (existingIndex >= 0) {
      // Update existing conversation
      const updatedConversations = [...currentState.conversations];
      updatedConversations[existingIndex] = conversation;
      set(chatStateAtom, {
        ...currentState,
        conversations: updatedConversations,
      });
    } else {
      // Add new conversation
      set(chatStateAtom, {
        ...currentState,
        conversations: [conversation, ...currentState.conversations],
      });
    }
  },
);

export const updateConversationAtom = atom(
  null,
  (get, set, conversationId: string, updates: Partial<ChatConversation>) => {
    const currentState = get(chatStateAtom);
    const updatedConversations = currentState.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, ...updates } : conv,
    );

    set(chatStateAtom, {
      ...currentState,
      conversations: updatedConversations,
    });
  },
);

export const removeConversationAtom = atom(
  null,
  (get, set, conversationId: string) => {
    const currentState = get(chatStateAtom);
    const filteredConversations = currentState.conversations.filter(
      (conv) => conv.id !== conversationId,
    );

    set(chatStateAtom, {
      ...currentState,
      conversations: filteredConversations,
      activeConversationId:
        currentState.activeConversationId === conversationId
          ? filteredConversations[0]?.id || null
          : currentState.activeConversationId,
    });
  },
);

// ===== MESSAGE MANAGEMENT ACTIONS =====

export const addMessageToConversationAtom = atom(
  null,
  (get, set, conversationId: string, message: ChatMessage) => {
    const currentState = get(chatStateAtom);
    const updatedConversations = currentState.conversations.map((conv) => {
      if (conv.id === conversationId) {
        const existingMessages = conv.messages || [];
        const existingIndex = existingMessages.findIndex(
          (m) => m.id === message.id,
        );

        if (existingIndex >= 0) {
          // Update existing message
          const updatedMessages = [...existingMessages];
          updatedMessages[existingIndex] = message;
          return { ...conv, messages: updatedMessages };
        } else {
          // Add new message
          return { ...conv, messages: [...existingMessages, message] };
        }
      }
      return conv;
    });

    set(chatStateAtom, {
      ...currentState,
      conversations: updatedConversations,
    });
  },
);

export const updateMessageInConversationAtom = atom(
  null,
  (
    get,
    set,
    conversationId: string,
    messageId: string,
    updates: Partial<ChatMessage>,
  ) => {
    const currentState = get(chatStateAtom);
    const updatedConversations = currentState.conversations.map((conv) => {
      if (conv.id === conversationId && conv.messages) {
        const updatedMessages = conv.messages.map((msg) =>
          msg.id === messageId ? { ...msg, ...updates } : msg,
        );
        return { ...conv, messages: updatedMessages };
      }
      return conv;
    });

    set(chatStateAtom, {
      ...currentState,
      conversations: updatedConversations,
    });
  },
);

export const setMessagesForConversationAtom = atom(
  null,
  (get, set, conversationId: string, messages: ChatMessage[]) => {
    const currentState = get(chatStateAtom);
    const updatedConversations = currentState.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, messages } : conv,
    );

    set(chatStateAtom, {
      ...currentState,
      conversations: updatedConversations,
    });
  },
);

// ===== LOADING AND ERROR STATE ACTIONS =====

export const setLoadingAtom = atom(null, (get, set, loading: boolean) => {
  const currentState = get(chatStateAtom);
  set(chatStateAtom, {
    ...currentState,
    isLoading: loading,
  });
});

export const setStreamingAtom = atom(
  null,
  (get, set, streaming: boolean, messageId?: string) => {
    const currentState = get(chatStateAtom);
    set(chatStateAtom, {
      ...currentState,
      isStreaming: streaming,
      streamingMessageId: streaming ? messageId || null : null,
    });
  },
);

export const setErrorAtom = atom(null, (get, set, error: string | null) => {
  const currentState = get(chatStateAtom);
  set(chatStateAtom, {
    ...currentState,
    error,
  });
});

// ===== PENDING RESPONSES MANAGEMENT =====

export const addPendingResponseAtom = atom(
  null,
  (get, set, responseId: string) => {
    const currentState = get(chatStateAtom);
    if (!currentState.pendingResponses.includes(responseId)) {
      set(chatStateAtom, {
        ...currentState,
        pendingResponses: [...currentState.pendingResponses, responseId],
      });
    }
  },
);

export const removePendingResponseAtom = atom(
  null,
  (get, set, responseId: string) => {
    const currentState = get(chatStateAtom);
    set(chatStateAtom, {
      ...currentState,
      pendingResponses: currentState.pendingResponses.filter(
        (id) => id !== responseId,
      ),
    });
  },
);

export const clearAllPendingResponsesAtom = atom(null, (get, set) => {
  const currentState = get(chatStateAtom);
  set(chatStateAtom, {
    ...currentState,
    pendingResponses: [],
  });
});

// ===== CONVERSATION METADATA ACTIONS =====

export const incrementPendingMessagesAtom = atom(
  null,
  (get, set, conversationId: string) => {
    const currentState = get(chatStateAtom);
    const updatedConversations = currentState.conversations.map((conv) =>
      conv.id === conversationId
        ? { ...conv, pendingMessages: (conv.pendingMessages || 0) + 1 }
        : conv,
    );

    set(chatStateAtom, {
      ...currentState,
      conversations: updatedConversations,
    });
  },
);

export const decrementPendingMessagesAtom = atom(
  null,
  (get, set, conversationId: string) => {
    const currentState = get(chatStateAtom);
    const updatedConversations = currentState.conversations.map((conv) =>
      conv.id === conversationId
        ? {
            ...conv,
            pendingMessages: Math.max(0, (conv.pendingMessages || 0) - 1),
          }
        : conv,
    );

    set(chatStateAtom, {
      ...currentState,
      conversations: updatedConversations,
    });
  },
);

export const updateConversationDraftAtom = atom(
  null,
  (get, set, conversationId: string, draftText: string) => {
    const currentState = get(chatStateAtom);
    const updatedConversations = currentState.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, draftText } : conv,
    );

    set(chatStateAtom, {
      ...currentState,
      conversations: updatedConversations,
    });
  },
);

// ===== UI ACTION ATOMS (ARCHITECTURAL COMPLIANCE) =====

/**
 * Action atom for submitting messages from UI components
 * Handles authentication checks and delegates to business logic
 */
export const submitMessageAtom = atom(
  null,
  async (
    get,
    set,
    params: { conversationId?: string; content: string; tags?: string[] },
  ) => {
    const { conversationId, content } = params;

    // Authentication check - this is UI-level validation
    const isAuthenticated = get(isAuthenticatedAtom);
    const tokens = get(authTokensAtom);

    if (!isAuthenticated || !tokens?.accessToken) {
      set(setErrorAtom, "Authentication required to send messages");
      return;
    }

    try {
      if (conversationId) {
        await set(sendMessageAtom, conversationId, content);
      } else {
        // Create new conversation first
        const newConversation = await set(createConversationAtom, "New Chat");
        if (newConversation) {
          await set(sendMessageAtom, newConversation.id, content);
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to send message";
      set(setErrorAtom, errorMessage);
      throw error;
    }
  },
);

/**
 * Action atom for creating new conversations from UI components
 * Handles authentication checks and delegates to business logic
 */
export const createNewConversationAtom = atom(
  null,
  async (get, set, title: string = "New Conversation") => {
    // Authentication check - this is UI-level validation
    const isAuthenticated = get(isAuthenticatedAtom);
    const tokens = get(authTokensAtom);

    if (!isAuthenticated || !tokens?.accessToken) {
      set(setErrorAtom, "Authentication required to create conversations");
      return;
    }

    try {
      return await set(createConversationAtom, title);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create conversation";
      set(setErrorAtom, errorMessage);
      throw error;
    }
  },
);

/**
 * Action atom for creating new threads from UI components
 */
export const createNewThreadAtom = atom(
  null,
  async (_get, set, params: { conversationId: string; title?: string }) => {
    const { conversationId, title = "New Thread" } = params;

    try {
      return await set(createThreadAtom, conversationId, title);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create thread";
      set(setErrorAtom, errorMessage);
      throw error;
    }
  },
);

/**
 * Action atom for deleting conversations from UI components
 */
export const deleteConversationAtom = atom(
  null,
  (_get, set, conversationId: string) => {
    set(removeConversationAtom, conversationId);
  },
);

/**
 * Action atom for clearing errors from UI components
 */
export const clearErrorAtom = atom(null, (_get, set) => {
  set(setErrorAtom, null);
});

/**
 * Action atom for clearing all pending responses from UI components
 */
export const clearAllPendingAtom = atom(null, (_get, set) => {
  set(clearAllPendingResponsesAtom);
});

// ===== BUSINESS LOGIC INTEGRATION ACTIONS =====

export const sendMessageAtom = atom(
  null,
  async (get, set, conversationId: string, content: string) => {
    try {
      // Ensure we have a valid workspace before sending message
      await set(ensureWorkspaceForChatAtom);

      // Check if oRPC connection is available via integration status
      const connectionStatus = get(orpcConnectionStatusAtom);

      // If not connected, queue the message instead of sending immediately
      if (!connectionStatus.connected) {
        console.log(
          "🔄 oRPC connection down - queueing message for later delivery",
        );

        const callbacks = get(chatHandlerCallbacksAtom);
        if (callbacks.onAddQueuedMessage) {
          const queuedMessageId = callbacks.onAddQueuedMessage({
            conversationId,
            content,
          });

          // Show current queue count
          const currentQueueCount = get(queuedMessagesAtom).length;
          console.log(
            `📥 Message queued with ID: ${queuedMessageId} (Total queued: ${currentQueueCount})`,
          );

          return queuedMessageId;
        } else {
          throw new Error("Chat handler not available for queueing");
        }
      }

      // Get device fingerprint for participant identification
      let participantId = get(participantIdAtom);

      // If no participant ID in atom, load it from storage first
      if (!participantId) {
        console.log("🔑 No participant ID in atom - loading from storage...");
        await set(loadParticipantIdAtom);
        participantId = get(participantIdAtom);
      }

      if (!participantId) {
        throw new Error("Failed to load or generate participant ID");
      }

      const deviceFingerprint = participantId.deviceFingerprint;
      // console.log("📝 Sending message with participant ID:", {
      //   participantId: participantId.id,
      //   fingerprint: deviceFingerprint.substring(0, 16) + "...",
      // });

      // Send message via chat handler (normal flow)
      const responseId = await chatHandler.getInstance().sendMessageAsync(
        conversationId,
        content,
        get(chatSettingsAtom),
        [], // Empty messages array - handler will get current messages
        deviceFingerprint, // Device fingerprint is required
      );

      console.log("📝 Message sent with response ID:", responseId);

      return responseId;
    } catch (error) {
      console.error("Failed to send message:", error);
      throw error;
    }
  },
);

export const createConversationAtom = atom(
  null,
  async (get, set, title: string, systemPrompt?: string) => {
    try {
      // Ensure we have a valid workspace before creating conversation
      await set(ensureWorkspaceForChatAtom);

      // Check if oRPC connection is available
      const connectionStatus = get(orpcConnectionStatusAtom);

      if (!connectionStatus.connected) {
        console.log(
          "🔄 oRPC connection down - creating temporary local conversation",
        );

        // Create a temporary local conversation for offline use
        const workspaceId = await set(ensureWorkspaceForChatAtom);
        const workspaceConfigInstance = workspaceConfig(envConfig);
        const temporaryConversation: ChatConversation = {
          id: workspaceConfigInstance.getDefaultConversationId(),
          title,
          workspaceId:
            workspaceId || workspaceConfigInstance.getDefaultWorkspaceId(),
          systemPrompt,
          createdAt: new Date(),
          updatedAt: new Date(),
          messages: [],
          pendingMessages: 0,
          isActive: true, // Mark as active since it's the new conversation
          isTemporary: true, // Mark as temporary
        };

        // Add the temporary conversation to state
        set(addConversationAtom, temporaryConversation);

        // Set as active conversation
        set(setActiveConversationAtom, temporaryConversation.id);

        console.log(
          "📝 Temporary conversation created:",
          temporaryConversation.id,
        );
        return temporaryConversation;
      }

      // Normal flow - create conversation via oRPC
      const conversation = await chatHandler
        .getInstance()
        .createConversation(title, systemPrompt);

      // Add the conversation to state
      set(addConversationAtom, conversation);

      // Set as active conversation
      set(setActiveConversationAtom, conversation.id);

      return conversation;
    } catch (error) {
      console.error("Failed to create conversation:", error);
      throw error;
    }
  },
);

export const createThreadAtom = atom(
  null,
  async (_get, set, conversationId: string, title?: string) => {
    try {
      // For now, return early since API doesn't support thread creation yet
      // When API supports it, this will call chatHandler.createThread()
      console.log(
        "Thread creation not yet supported by API for conversation:",
        conversationId,
        "with title:",
        title || "Default Thread",
      );
      set(setErrorAtom, "Thread creation will be available in a future update");
      return null;

      // Future implementation:
      // const thread = await chatHandler.createThread(conversationId, title);
      // set(addThreadAtom, thread);
      // set(setActiveThreadAtom, thread.id);
      // return thread;
    } catch (error) {
      console.error("Failed to create thread:", error);
      set(
        setErrorAtom,
        error instanceof Error ? error.message : "Failed to create thread",
      );
      throw error;
    }
  },
);

// Atom to ensure there's always a valid workspace for chat operations
export const ensureWorkspaceForChatAtom = atom(null, async (get, set) => {
  let activeWorkspaceId = get(activeWorkspaceIdAtom);
  const workspaces = get(workspacesAtom);

  // If no active workspace and no workspaces exist, create a default workspace
  if (!activeWorkspaceId && workspaces.length === 0) {
    console.log("🏗️ Creating default workspace for chat operation");
    const defaultWorkspace = await set(createWorkspaceAtom, {
      name: "Default Workspace",
      type: "chat" as const,
      color: "#3b82f6",
      description: "Default chat workspace",
    });
    activeWorkspaceId = defaultWorkspace.id;

    // Update chat handler with new workspace ID
    chatHandler.getInstance().setWorkspaceId(activeWorkspaceId || "");
  } else if (!activeWorkspaceId && workspaces.length > 0) {
    // If workspaces exist but none is active, activate the first one
    activeWorkspaceId = workspaces[0].id;
    set(activeWorkspaceIdAtom, activeWorkspaceId);
    chatHandler.getInstance().setWorkspaceId(activeWorkspaceId || "");
  }

  return activeWorkspaceId;
});

// Data loading is now handled through callbacks in the chat handler
// These atoms are replaced by the callback-based approach for real-time updates

// ===== CHAT HANDLER INITIALIZATION =====

// Context atom to store workspace and auth information for handlers
export const chatContextAtom = atom<{
  workspaceId: string | null;
  accessToken: string | null;
}>({
  workspaceId: null,
  accessToken: null,
});

// ===== CHAT INITIALIZATION (NEW ARCHITECTURE) =====

// Initialize chat system atom - triggers handler initialization via callback pattern
export const initializeChatSystemAtom = atom(
  null,
  async (get, _set, config: { workspaceId: string; authToken?: string }) => {
    try {
      console.log(
        "💬 State: Requesting chat system initialization via handler callback",
      );

      // Trigger handler to initialize chat system - this follows proper callback pattern
      const callbacks = get(chatHandlerCallbacksAtom);
      if (callbacks.onInitializeChatSystem) {
        await callbacks.onInitializeChatSystem(
          config.workspaceId,
          config.authToken,
        );
        console.log(
          "✅ State: Chat system initialization requested via handler",
        );
      } else {
        console.warn("⚠️ State: Chat handler not available for initialization");
        throw new Error("Chat handler not available for initialization");
      }
    } catch (error) {
      console.error("❌ State: Chat system initialization failed:", error);
      throw error;
    }
  },
);

// Cleanup chat system atom
export const cleanupChatSystemAtom = atom(null, async (get, _set) => {
  try {
    console.log(
      "💬 State: Requesting chat system cleanup via handler callback",
    );

    const callbacks = get(chatHandlerCallbacksAtom);
    if (callbacks.onCleanupChatSystem) {
      await callbacks.onCleanupChatSystem();
      console.log("✅ State: Chat system cleanup requested via handler");
    } else {
      console.warn("⚠️ State: Chat handler not available for cleanup");
    }
  } catch (error) {
    console.error("❌ State: Chat system cleanup failed:", error);
    throw error;
  }
});

// ===== AUTHENTICATION INTEGRATION =====

// Atom to update chat context when auth state changes
export const updateChatAuthTokenAtom = atom(
  null,
  async (get, set, accessToken: string | null) => {
    const currentContext = get(chatContextAtom);

    // 🚨 CRITICAL: Only update if the token actually changed
    if (currentContext.accessToken === accessToken) {
      console.log("🔄 Auth token unchanged - skipping chat handler update");
      return;
    }

    const newContext = {
      ...currentContext,
      accessToken,
    };

    set(chatContextAtom, newContext);

    // Notify handler of context change (state → handler direction)
    // The handler has its own idempotent guards to prevent unnecessary re-initialization
    try {
      await chatHandler
        .getInstance()
        .initializeWithContext({ ...newContext, envConfig });
    } catch (error) {
      console.warn("Failed to update chat handler context:", error);
    }
  },
);

// ===== LEGACY COMPATIBILITY ATOMS =====
// These atoms provide compatibility with existing components that use legacy names

export const chatConversationsAtom = conversationsAtom;
export const activeChatConversationIdAtom = atom(
  (get) => get(chatStateAtom).activeConversationId,
);
export const activeChatConversationAtom = activeConversationAtom;
export const activeChatMessagesAtom = atom((get) => {
  const activeConversation = get(activeConversationAtom);
  return activeConversation?.messages || [];
});
export const chatLoadingAtom = isLoadingAtom;
export const chatErrorAtom = errorAtom;

export const createChatConversationAtom = createConversationAtom;
export const deleteChatConversationAtom = removeConversationAtom;
export const addChatMessageAtom = addMessageToConversationAtom;
export const updateChatMessageAtom = updateMessageInConversationAtom;
export const updateChatConversationTitleAtom = updateConversationAtom;

export const setChatLoadingAtom = setLoadingAtom;
export const setChatErrorAtom = setErrorAtom;

export const clearChatErrorAtom = atom(null, (_get, set) => {
  set(setErrorAtom, null);
});

// Draft text storage for active conversation - separate read/write to prevent subscription cascades
const _activeChatDraftTextAtom = atomWithStorage<string>(
  "ive-active-chat-draft",
  "",
);

// Read-only atom (doesn't trigger re-renders on writes from same component)
export const activeChatDraftTextAtom = atom(
  (get) => get(_activeChatDraftTextAtom),
  (_get, set, update: string) => {
    set(_activeChatDraftTextAtom, update);
  },
);

// ===== THREAD MANAGEMENT ACTIONS =====

export const addThreadAtom = atom(
  null,
  async (get, set, thread: ChatThread) => {
    const currentState = get(chatStateAtom);
    const existingIndex = currentState.threads.findIndex(
      (t) => t.id === thread.id,
    );

    if (existingIndex >= 0) {
      // Update existing thread
      const updatedThreads = [...currentState.threads];
      updatedThreads[existingIndex] = thread;
      set(chatStateAtom, {
        ...currentState,
        threads: updatedThreads,
      });
    } else {
      // Add new thread
      const updatedThreads = [...currentState.threads, thread];

      // Auto-select the first thread for the active conversation if none is selected
      const shouldAutoSelect =
        thread.conversationId === currentState.activeConversationId &&
        !currentState.activeThreadId;

      set(chatStateAtom, {
        ...currentState,
        threads: updatedThreads,
        activeThreadId: shouldAutoSelect
          ? thread.id
          : currentState.activeThreadId,
      });

      // If auto-selecting, trigger SSE subscription for the thread
      if (shouldAutoSelect) {
        console.log(
          "🔌 Auto-selected thread - starting SSE subscription:",
          thread.id,
        );
        try {
          const workspaceId = get(activeWorkspaceIdAtom);

          if (workspaceId) {
            await orpcIntegration.startSSESubscription(
              workspaceId,
              thread.conversationId,
              thread.id,
            );
            console.log("✅ SSE subscription started for auto-selected thread");
          } else {
            console.warn("⚠️ No workspace ID available for SSE subscription");
          }
        } catch (error) {
          console.error(
            "❌ Failed to start SSE subscription for auto-selected thread:",
            error,
          );
        }
      }
    }
  },
);

export const setActiveThreadAtom = atom(
  null,
  async (get, set, threadId: string | null) => {
    const currentState = get(chatStateAtom);
    set(chatStateAtom, {
      ...currentState,
      activeThreadId: threadId,
    });

    // Load messages for the selected thread
    if (threadId) {
      const thread = currentState.threads.find((t) => t.id === threadId);
      if (thread) {
        try {
          console.log("🔌 Loading messages for thread:", thread.id);
          await chatHandler
            .getInstance()
            .loadMessages(thread.conversationId, 50);

          // Start SSE subscription now that we have both conversation and thread
          const workspaceId = get(activeWorkspaceIdAtom);

          if (workspaceId) {
            console.log(
              "🔌 Starting SSE subscription for active conversation/thread",
            );
            await orpcIntegration.startSSESubscription(
              workspaceId,
              thread.conversationId,
              threadId,
            );
          } else {
            console.warn("No workspace ID available for SSE subscription");
          }
        } catch (error) {
          console.warn("Failed to load messages for thread:", error);
        }
      }
    }
  },
);

export const removeThreadAtom = atom(null, (get, set, threadId: string) => {
  const currentState = get(chatStateAtom);
  const filteredThreads = currentState.threads.filter(
    (thread) => thread.id !== threadId,
  );

  set(chatStateAtom, {
    ...currentState,
    threads: filteredThreads,
    activeThreadId:
      currentState.activeThreadId === threadId
        ? filteredThreads[0]?.id || null
        : currentState.activeThreadId,
  });
});
